import axios from 'axios';
import { AssetPrice, HistoricalData } from './api';

/**
 * Marketstack API服务
 * 需要API密钥
 * 支持股票和指数
 */
class MarketstackService {
  private client = axios.create({
    baseURL: 'https://api.marketstack.com/v2',
    timeout: 10000,
  });

  // API密钥（优先级：用户配置 > 环境变量）
  private apiKey: string | null = null;

  constructor() {
    // 优先级 1: 优先从 settingsStore（用户配置）读取
    try {
      const { useSettingsStore } = require('@/stores/settingsStore');
      const apiKeys = useSettingsStore.getState().apiKeys;
      if (apiKeys?.marketstack && apiKeys.marketstack.trim() !== '') {
        this.apiKey = apiKeys.marketstack;
        return; // 如果找到用户配置，直接返回，不再检查环境变量
      }
    } catch (e) {
      // 忽略错误，继续检查环境变量
    }
    
    // 优先级 2: 如果没有用户配置，尝试从环境变量读取（向后兼容）
    this.apiKey = (import.meta as any)?.env?.VITE_MARKETSTACK_API_KEY || null;
  }

  setApiKey(apiKey: string | null): void {
    this.apiKey = apiKey;
  }

  async getStockPrice(symbol: string): Promise<AssetPrice> {
    if (!this.apiKey) throw new Error('Marketstack API key is required');
    
    try {
      // 首先使用 v2 API 验证 ticker 是否存在: /v2/tickers/{symbol}
      const v2Client = axios.create({
        baseURL: 'https://api.marketstack.com/v2',
        timeout: 10000,
      });
      
      const tickerResponse = await v2Client.get(`/tickers/${symbol}`, {
        params: {
          access_key: this.apiKey,
        },
      });

      // v2/tickers 返回 ticker 信息，但不包含价格
      // 需要使用 v1 API 获取价格数据
      const v1Client = axios.create({
        baseURL: 'https://api.marketstack.com/v1',
        timeout: 10000,
      });
      
      const priceResponse = await v1Client.get('/eod/latest', {
        params: {
          symbols: symbol,
          access_key: this.apiKey,
        },
      });

      const data = priceResponse.data.data?.[0];
      if (!data) throw new Error(`Stock not found: ${symbol}`);

      // 使用 v2 ticker 信息中的名称（如果可用）
      const tickerName = tickerResponse.data.name || symbol.toUpperCase();

      return {
        id: symbol.toLowerCase(),
        symbol: symbol.toUpperCase(),
        name: tickerName,
        current_price: data.close || 0,
        price_change_24h: (data.close || 0) - (data.open || 0),
        price_change_percentage_24h: data.close && data.open ? ((data.close - data.open) / data.open) * 100 : 0,
        last_updated: data.date ? new Date(data.date).toISOString() : new Date().toISOString(),
        source_url: 'https://api.marketstack.com/v2',
      };
    } catch (error: any) {
      // 如果 v2 API 失败，直接使用 v1 API
      if (error.response?.status === 403 || error.response?.data?.error?.code === 'function_access_restricted') {
        const v1Client = axios.create({
          baseURL: 'https://api.marketstack.com/v1',
          timeout: 10000,
        });
        
        const v1Response = await v1Client.get('/eod/latest', {
          params: {
            symbols: symbol,
            access_key: this.apiKey,
          },
        });

        const data = v1Response.data.data?.[0];
        if (!data) throw new Error(`Stock not found: ${symbol}`);

        return {
          id: symbol.toLowerCase(),
          symbol: symbol.toUpperCase(),
          name: symbol.toUpperCase(),
          current_price: data.close || 0,
          price_change_24h: (data.close || 0) - (data.open || 0),
          price_change_percentage_24h: data.close && data.open ? ((data.close - data.open) / data.open) * 100 : 0,
          last_updated: data.date ? new Date(data.date).toISOString() : new Date().toISOString(),
          source_url: 'https://api.marketstack.com/v1',
        };
      }
      throw error;
    }
  }

  async getIndexPrice(symbol: string): Promise<AssetPrice> {
    return this.getStockPrice(symbol); // Marketstack uses same endpoint for indices
  }

  async getHistoricalData(symbol: string, days: number = 7): Promise<HistoricalData> {
    if (!this.apiKey) throw new Error('Marketstack API key is required');
    
    const response = await this.client.get('/eod', {
      params: {
        symbols: symbol,
        access_key: this.apiKey,
        limit: days,
        sort: 'DESC',
      },
    });

    const data = response.data.data || [];
    
    const prices: [number, number][] = data.map((item: any) => [
      new Date(item.date).getTime(),
      item.close || 0,
    ]).reverse();

    const market_caps: [number, number][] = prices.map(([time, price]) => [time, price * 1000000]);
    const total_volumes: [number, number][] = data.map((item: any) => [
      new Date(item.date).getTime(),
      item.volume || 0,
    ]).reverse();

    return { prices, market_caps, total_volumes };
  }
}

export const marketstackService = new MarketstackService();
